import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { SettingsData } from "src/api/General/settings";

dayjs.extend(utc);
dayjs.extend(timezone);

export function calculateEndDate(
  startDate: Date,
  durationMs: number,
  workingTimeDetails: SettingsData["workingTimeDetails"],
  timezone: string
): Date {
  const workingHours: {
    [dayOfWeek: number]: { startTime: string; endTime: string } | null;
  } = getWorkingHours(workingTimeDetails);
  let remainingDuration = durationMs;
  let currentDateTime = dayjs(startDate.toISOString()).tz(timezone);
  while (remainingDuration > 0) {
    const dayOfWeek = currentDateTime.day(); // 0 (Sunday) to 6 (Saturday)
    const workHours = workingHours[dayOfWeek];
    if (workHours) {
      // Parse work start and end times
      const [startHour, startMinute] = workHours.startTime
        .split(":")
        .map(Number);
      const [endHour, endMinute] = workHours.endTime.split(":").map(Number);

      // Set work start and end times for the current day
      const workStart = currentDateTime
        .hour(startHour)
        .minute(startMinute)
        .second(0)
        .millisecond(0);

      const workEnd = currentDateTime
        .hour(endHour)
        .minute(endMinute)
        .second(0)
        .millisecond(0);

      if (currentDateTime.isBefore(workStart)) {
        // Move to the start of working hours
        currentDateTime = workStart;
      }

      if (currentDateTime.isBefore(workEnd)) {
        // Calculate available working time for the current day
        const timeUntilWorkEnd = workEnd.diff(currentDateTime, "millisecond");
        const timeToAdd = Math.min(remainingDuration, timeUntilWorkEnd);

        // Add timeToAdd to currentDateTime
        currentDateTime = currentDateTime.add(timeToAdd, "millisecond");
        remainingDuration -= timeToAdd;
      }
    }

    if (remainingDuration > 0) {
      // Move to the next day at the start of the day in the same timezone
      currentDateTime = currentDateTime.add(1, "day").startOf("day");
    }
  }

  // Return the end date as a JavaScript Date object
  return currentDateTime.toDate();
}

function getWorkingHours(
  workingTimeDetails: SettingsData["workingTimeDetails"]
): {
  [dayOfWeek: number]: { startTime: string; endTime: string } | null;
} {
  return {
    0: getTimeRangeForDay(workingTimeDetails.days[0]),
    1: getTimeRangeForDay(workingTimeDetails.days[1]),
    2: getTimeRangeForDay(workingTimeDetails.days[2]),
    3: getTimeRangeForDay(workingTimeDetails.days[3]),
    4: getTimeRangeForDay(workingTimeDetails.days[4]),
    5: getTimeRangeForDay(workingTimeDetails.days[5]),
    6: getTimeRangeForDay(workingTimeDetails.days[6]),
  };
  function getTimeRangeForDay(isWorking: boolean) {
    return isWorking
      ? {
          startTime: addMillisecondsToTime(workingTimeDetails.startTime, 0),
          endTime: addMillisecondsToTime(
            workingTimeDetails.startTime,
            workingTimeDetails.workingTimePerDay
          ),
        }
      : null;
  }
}

function addMillisecondsToTime(time: string, msToAdd: number): string {
  // Parse the time string into hours and minutes
  const [hoursStr, minutesStr] = time.split(":");
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  // Compute the total milliseconds of the input time
  const totalMilliseconds = (hours * 3600 + minutes * 60) * 1000;

  // Add the milliseconds to the total milliseconds
  let finalMilliseconds = totalMilliseconds + msToAdd;

  // Wrap around the 24-hour clock if necessary
  const msInDay = 24 * 60 * 60 * 1000;
  finalMilliseconds = ((finalMilliseconds % msInDay) + msInDay) % msInDay; // Ensure positive value

  // Compute the final hours and minutes
  const finalHours = Math.floor(finalMilliseconds / (3600 * 1000));
  const finalMinutes = Math.floor(
    (finalMilliseconds % (3600 * 1000)) / (60 * 1000)
  );

  // Format the final time string with leading zeros if necessary
  const finalHoursStr = finalHours.toString().padStart(2, "0");
  const finalMinutesStr = finalMinutes.toString().padStart(2, "0");

  return `${finalHoursStr}:${finalMinutesStr}`;
}
